import React, { useEffect } from 'react'
import { Box, IconButton, Button, Typography, useTheme, TextField, Divider, Autocomplete } from "@mui/material";
import { tokens } from "../../theme";
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import Grid from '@mui/material/Grid';
import AddMemoDatePicker from "../students/moreInfo/AddMemoDatePicker";
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Popover from '@mui/material/Popover';
import Axios from "axios";
import Alert from '../../components/alert/Alert';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
// import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// import Checkbox from '@mui/material/Checkbox';
import dayjs from 'dayjs';
import { countryPhoneCodes } from '../weekly/studentButtonMenu/countryPhoneCode';
import { SocketContext } from '../../socket';
import { getAuthHeader } from '../../utils/authToken';
import { firstInvalidEmail, PARENT_EMAIL_LABELS } from '../../utils/emailShape';

const QuickAdd = () => {
    const socket = React.useContext(SocketContext);
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const gridItemStyle2 = {
        '& > :not(style)': { m: 1, width: '20rem' }
    };

    // for email code (ex. MK1)
    const [emailCode, setEmailCode] = React.useState("MK1");

    // memo title and content (controlled to avoid IME last-character loss)
    const [memoTitle, setMemoTitle] = React.useState("");
    const [memoContent, setMemoContent] = React.useState("");

    const [potentialStudentCreated, setPotentialStudentCreated] = React.useState(false);

    // for alert
    const [successAlertOpen, setSuccessAlertOpen] = React.useState(false);
    const [errorAlertOpen, setErrorAlertOpen] = React.useState(false);
    const [successMsg, setSuccessMsg] = React.useState('');
    const [errorMsg, setErrorMsg] = React.useState('');
    const [socketConnected, setSocketConnected] = React.useState(false);
    const [parentEmailWarning, setParentEmailWarning] = React.useState('');

    /*
     * popover for quick add
     */ 
    const [quickAddAnchorEl, setQuickAddAnchorEl] = React.useState(null);
    const handleQuickAddClick = (event) => { setQuickAddAnchorEl(event.currentTarget) };
    const handleQuickAddClose = () => { 
        setQuickAddAnchorEl(null) 
        setEmailCode("MK1")
    };
    const quickAddOpen = Boolean(quickAddAnchorEl);
    const quickAddId = quickAddOpen ? 'simple-popover' : undefined;

    useEffect(() => {
        if (!quickAddOpen || !socket) {
            return;
        }

        // The socket is shared with whatever page is open (Missed Calls owns it while it is mounted, and the voice
        // feeds ride on it). This popover is a guest on it: it connects if nobody has yet, and on close it takes
        // away only its OWN listeners and leaves the connection alone.
        if (!socket.connected) socket.connect();

        const onConnect = () => setSocketConnected(true);
        const onDisconnect = () => setSocketConnected(false);
        const onConnectError = () => setSocketConnected(false);
        const onNumberTaken = () => {
            setPotentialStudentCreated(true);
            reinitialize();
        };

        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);
        socket.on('connect_error', onConnectError);
        socket.on('consultation_number_taken', onNumberTaken);

        return () => {
            // By handler, never socket.off('connect'): the single-argument form removes EVERY listener for the
            // event, which used to strip the open page's own reconnect handling as this popover closed.
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
            socket.off('connect_error', onConnectError);
            socket.off('consultation_number_taken', onNumberTaken);
            // Deliberately NOT socket.disconnect(): that killed the shared connection (and, because a client-side
            // disconnect sets skipReconnect, it did not come back) every time the popover closed.
            setSocketConnected(false);
        };
    }, [quickAddOpen, socket]);

    // for parent country phone code
    const [parentCountryPhoneCode, setParentCountryPhoneCode] = React.useState(countryPhoneCodes.find((countryPhoneCode) => countryPhoneCode.name === "United States"));

    function isNumeric(value) {
        return /^-?\d+$/.test(value);
    }

    const handleQuickAdd = async (event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        let student_uuid = null;

        let email_code = data.get('email_code');
        if (email_code && email_code === "") {
            email_code = null;
        }

        // should it be not null and forced to put in the form?
        let consultation_number = data.get('consultation_number');
        if (consultation_number && consultation_number === "") {
            consultation_number = null;
        }

        if (consultation_number && !isNumeric(consultation_number)) {
            setErrorMsg("Consultation number should be numeric!");
            setErrorAlertOpen(true);
            return;
        }

        if (data.get('parent_phone') !== "" && parentCountryPhoneCode === null) {
            setErrorMsg("Please select a country phone code!");
            setErrorAlertOpen(true);
            return;
        }

        // Quick-add writes parent_email with no schema behind it. A note saved here would end up
        // in the To: header of every automated parent email and fail the whole message, so it's
        // checked before the POST (the server rejects it as well).
        const emailError = firstInvalidEmail({ parent_email: data.get('parent_email') }, PARENT_EMAIL_LABELS);
        if (emailError) {
            setErrorMsg(emailError);
            setErrorAlertOpen(true);
            return;
        }

        // One consultation number per family: a known parent email may only be quick-added
        // again with the family's existing consultation number (sibling add). A new number
        // for a known email is hard-blocked (server enforces the same rule).
        const parent_email = data.get('parent_email');
        if (parent_email) {
            try {
                const dupCheck = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/students/parent-email/check`, {
                    params: { email: parent_email.trim() },
                    headers: { Authorization: getAuthHeader() },
                });
                if (dupCheck.data.exists) {
                    const familyNumbers = [...new Set(dupCheck.data.consultation_numbers || [])];
                    const reusesFamilyNumber = consultation_number && familyNumbers.some((n) => Number(n) === Number(consultation_number));
                    if (!reusesFamilyNumber) {
                        setErrorMsg(familyNumbers.length > 0
                            ? `This parent email already exists with consultation number ${familyNumbers.join(', ')}. Reuse that number instead of a new one.`
                            : 'This parent email already exists (no consultation number on file).');
                        setErrorAlertOpen(true);
                        return;
                    }
                }
            } catch {
                // server enforces the same rule on submit
            }
        }

        await Axios.get(`${process.env.REACT_APP_URL}/api/v1/students/consultation-number/check/${consultation_number}`, {
            headers: {
                Authorization: getAuthHeader(),
            },
        }).then(async (response) => {
            if (response.data.exists) {
                if (!window.confirm("Consultation number already exists. Do you want to continue?")) {
                    return;
                }
            }

            await Axios.post(`${process.env.REACT_APP_URL}/api/v1/students/quick-add`, {
                email_code: email_code,
                consultation_number: consultation_number,
                parent_phone: data.get('parent_phone'),
                parent_email: data.get('parent_email'),
                parent_gender: gender,
                parent_country_phone_code: parentCountryPhoneCode ? parentCountryPhoneCode.code : "+1",
            }, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then(async (response) => {
                student_uuid = response.data.student_uuid;
                socket?.emit('consultation_number_taken', consultation_number);
                if (memoTitle !== "" || memoContent !== "") {
                    await Axios.post(`${process.env.REACT_APP_URL}/api/v1/students/${student_uuid}/memos/quickadd`, {
                        title: memoTitle,
                        content: memoContent,
                        reminder: reminderDate !== null ? reminderDate.toDate() : null,
                        bookmark: bookmarked,
                        is_info_mail: true,
                        is_sent: checked ? true : false,
                    }, {
                        headers: {
                            Authorization: getAuthHeader(),
                        },
                    }).then(async (response) => {
                        await Axios.post(`${process.env.REACT_APP_URL}/api/v1/students/${student_uuid}/info-mails`, {
                            date: checked ? dayjs(infoMailDate) : null,
                            is_sent: checked ? true : false,
                        }, {
                            headers: {
                                Authorization: getAuthHeader(),
                            },
                        }).then((response) => {
                            setSuccessMsg("Successfully added a student with memo!");
                            setSuccessAlertOpen(true);
                            setPotentialStudentCreated(true);
                            reinitialize();
                        }).catch((error) => {
                            setErrorMsg(error.response?.data?.message || error.response?.data?.error || "Something went wrong");
                            setErrorAlertOpen(true);
                        });
                    }).catch((error) => {
                        setErrorMsg(error.response?.data?.message || error.response?.data?.error || "Something went wrong");
                        setErrorAlertOpen(true);
                    });
                } else {
                    setSuccessMsg("Successfully added a student!");
                    setSuccessAlertOpen(true);
                    setPotentialStudentCreated(true);
                    reinitialize();
                }
            }).catch((error) => {
                console.log(error.response?.data?.message || error.response?.data?.error);
                setErrorMsg(error.response?.data?.message || error.response?.data?.error || "Something went wrong");
                setErrorAlertOpen(true);
            });
        }).catch((error) => {
            console.log(error.response?.data?.message || error.response?.data?.error);
            setErrorMsg(error.response?.data?.message || error.response?.data?.error || "Something went wrong");
            setErrorAlertOpen(true);
            return;
        });
    };

    // for reminder
    const [reminderDate, setReminderDate] = React.useState(null);
    
    // for bookmark
    const [bookmarked, setBookmarked] = React.useState(false);

    // for info mail
    const [checked, setChecked] = React.useState(false);
    const handleCheckBoxChange = (event) => {
        setChecked(event.target.checked);
    };
    const [infoMailDate, SetInfoMailDate] = React.useState(null);

    // for parent gender
    const [gender, setGender] = React.useState('');
    const handleGenderChange = (event) => {
        setGender(event.target.value);
    };

    const reinitialize = () => {
        setReminderDate(null);
        setGender('');
        setBookmarked(false);
        setParentEmailWarning('');
        setMemoTitle("");
        setMemoContent("");
        document.getElementById("quick-add-form").reset();
    }

    const handleParentEmailBlur = async (e) => {
        const email = e.target.value.trim();
        if (!email) {
            setParentEmailWarning('');
            return;
        }
        try {
            const response = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/students/parent-email/check`, {
                params: { email },
                headers: { Authorization: getAuthHeader() },
            });
            if (response.data.exists) {
                setParentEmailWarning(`Email exists (${response.data.consultation_numbers.join(', ')}).`);
            } else {
                setParentEmailWarning('');
            }
        } catch {
            setParentEmailWarning('');
        }
    };

    // for consultation number
    const [newConsultationNumber, setNewConsultationNumber] = React.useState(null);

    React.useEffect(() => {
        async function fetchLastNumberOfConsNum() {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/students/consultation-number/last-number`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then((response) => {
                let lastNumber = response.data;
                if (lastNumber === null) {
                    lastNumber = 0;
                }
                setNewConsultationNumber(lastNumber + 1);
            }).catch((error) => {
                console.log(error.response?.data?.message || error.response?.data?.error);

                if (error.response?.status === 404) {
                    // ignore because it means there is no consultation number
                }
            });
        }

        fetchLastNumberOfConsNum();
        setPotentialStudentCreated(false);
    }, [potentialStudentCreated]);


    return (<>
        <IconButton onClick={handleQuickAddClick}>
            <PersonAddAltOutlinedIcon />
        </IconButton>
        <Popover
            id={quickAddId}
            open={quickAddOpen}
            anchorEl={quickAddAnchorEl}
            onClose={handleQuickAddClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            sx={{
                '& .MuiPopover-paper': {
                    border: '1px solid',
                    borderRadius: '20px',
                    backgroundColor: colors.primary[750],

                },
            }}
        >
            <Box 
                component="form" 
                id='quick-add-form'
                onSubmit={handleQuickAdd} 
                noValidate
                sx={{ 
                    p: 3, 
                    width: 1000,
                    display: "flex", 
                    flexDirection: "column", 
                }}
            >
                <Typography variant="h4" fontWeight="bold" sx={{ mx: "auto", mb: 1 }}>
                    Quick Add for Potential Students
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box 
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                    <Grid container direction="column" alignItems="center" flex={6}>
                        <Typography variant="h6" fontWeight="bold">
                            Memo
                        </Typography>
                        <Grid item sx={{ my: "0.5rem" }}>
                            <AddMemoDatePicker reminderDate={reminderDate} setReminderDate={setReminderDate} />
                        </Grid>
                        <Grid item sx={{ my: "0.5rem" }}>
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        checked={bookmarked}
                                        onChange={(e) => setBookmarked(e.target.checked)}
                                        icon={<BookmarkBorderIcon />}
                                        checkedIcon={<BookmarkIcon />}
                                        sx={{
                                            color: colors.grey[400],
                                            '&.Mui-checked': {
                                                color: '#ed6c02',
                                            },
                                        }}
                                    />
                                }
                                label="Bookmark this memo"
                                sx={{
                                    '& .MuiFormControlLabel-label': {
                                        color: 'primary.main',
                                        fontSize: '14px',
                                        ml: 0.5,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item sx={{ my: "0.5rem" }}>
                            <TextField
                                id="filled-basic"
                                label="Title"
                                variant="filled"
                                name='title'
                                value={memoTitle}
                                onChange={(e) => setMemoTitle(e.target.value)}
                                sx={{ width: "20rem" }}
                            />
                        </Grid>
                        <Grid item sx={{ my: "0.5rem" }}>
                            <TextField
                                id="filled-basic memo-textarea"
                                className='memo-textarea'
                                label="Memo"
                                variant="filled"
                                name='content'
                                value={memoContent}
                                onChange={(e) => setMemoContent(e.target.value)}
                                multiline={true}
                                rows={8}
                                sx={{
                                    borderRadius: "5px",
                                    width: "20rem",
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Grid container direction="column" alignItems="center" flex={4}>
                        <Typography variant="h6" fontWeight="bold">
                            Infomation
                        </Typography>
                        <Grid item sx={gridItemStyle2}>
                            <TextField
                                id="email_code"
                                name="email_code"
                                label="Email Code"
                                variant="filled"
                                value={emailCode}
                                onChange={(e) => setEmailCode(e.target.value)}
                            />
                        </Grid>
                        <Grid item sx={gridItemStyle2}>
                            <TextField
                                id="consultation_number"
                                name="consultation_number"
                                label="Consultation Number"
                                variant="filled"
                                value={newConsultationNumber}
                                onChange={(e) => setNewConsultationNumber(e.target.value)}
                            />
                        </Grid>
                        <Grid item sx={gridItemStyle2}>
                            <Autocomplete
                                id="country-phone-code"
                                value={parentCountryPhoneCode}
                                options={countryPhoneCodes}
                                getOptionLabel={(option) => option.code + " " + option.name}
                                sx={{ width: "50%" }}
                                renderInput={(params) => <TextField {...params} label="Country Phone Code" />}
                                onChange={(e, value) => {
                                    setParentCountryPhoneCode(value);
                                }}
                                isOptionEqualToValue={(option, value) => option.code === value.code}
                            />
                            <TextField
                                id="parent_phone"
                                name="parent_phone"
                                label="Parent's Phone"
                                variant="filled"
                            />
                        </Grid>
                        <Grid item sx={gridItemStyle2}>
                            <TextField
                                id="parent_email"
                                name="parent_email"
                                label="Parent's Email"
                                variant="filled"
                                onBlur={handleParentEmailBlur}
                                helperText={parentEmailWarning}
                                FormHelperTextProps={parentEmailWarning ? { sx: { color: '#ed6c02' } } : undefined}
                            />
                        </Grid>
                        <Grid item sx={gridItemStyle2}>
                            <FormControl variant="filled" sx={{ gridColumn: "span 1" }}>
                                <InputLabel id="demo-simple-select-filled-label">Parent's Gender</InputLabel>
                                <Select
                                    labelId="demo-simple-select-filled-label"
                                    id="demo-simple-select-filled"
                                    value={gender}
                                    onChange={handleGenderChange}
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    <MenuItem value={"Male"}>Male</MenuItem>
                                    <MenuItem value={"Female"}>Female</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
                <Button type='submit' color="secondary" variant="contained" sx={{ width: "20rem", mx: "auto", mt: 3 }} >
                    Add Student with Memo
                </Button>
            </Box>
            <Alert 
                successAlertOpen={successAlertOpen} 
                setSuccessAlertOpen={setSuccessAlertOpen}
                errorAlertOpen={errorAlertOpen}
                setErrorAlertOpen={setErrorAlertOpen}
                successMsg={successMsg}
                errorMsg={errorMsg}
                vert='top'
                horiz='right'
            />
        </Popover>
    </>)
}

export default QuickAdd